import { SaveButton, useForm } from '@refinedev/antd';
import { ISittingRecord } from '../../../interfaces/sitting';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Upload,
} from 'antd';
import DatePicker from '../../../components/form-item/DatePicker';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { ExperienceActionSelect } from '../../../components/form-item/ExperienceActionSelect';
import { BaseKey, useMany } from '@refinedev/core';
import { IChild } from '../../../interfaces/child';
import { ImpersonateButton } from '../../users/impersonate-button';
import { format, parse } from 'date-fns';
import { fetcher } from '../../../utils/fetcher';

const fields = [
  'id',
  'startTime',
  'durationHours',
  'memo',
  'sitterMessage',
  'operatorMessage',
  'transportationExpensePrice',
  'transportationExpenseMemo',
  'status',
  'educationalMaterial',
  'childIds',
  'token',
  {
    recordActivities: [
      'id',
      'title',
      'description',
      'actionId',
      'actionTitle',
      'actionEpisode',
      'actionDescription',
      'actionAdvice',
      {
        action: ['id', 'name', 'title', 'description', 'advice'],
        images: ['id', 'url'],
      },
    ],
    transportationExpenseImages: ['id', 'url'],
    sitter: ['id', 'name'],
    schedule: ['id', 'startTime', 'durationHours', 'userId'],
  },
];

type Props = {
  recordId?: BaseKey;
  childList: IChild[];
};

export const ReportForm = ({ recordId, childList }: Props) => {
  const { form, formProps, saveButtonProps, onFinish, queryResult } = useForm<ISittingRecord>({
    resource: 'sittingRecord',
    id: recordId,
    action: recordId ? 'edit' : 'create',
    meta: {
      fields,
    },
    mutationMeta: {
      fields: [{ sittingRecord: fields }],
    },
  });
  const isDirty = form.isFieldsTouched();

  const recordActivities = Form.useWatch('recordActivities', form) || [];
  const { data: baseActions } = useMany({
    resource: 'experienceActions',
    ids: recordActivities.flatMap((recordActivity: any) =>
      recordActivity.actionId ? [recordActivity.actionId] : [],
    ),
    queryOptions: { enabled: !!recordActivities.length },
    meta: { fields: ['id', 'name', 'title', 'description', 'advice'] },
  });

  const resetRecordActivity = (index: number) => {
    const recordActivity = recordActivities[index];
    const newAction = baseActions?.data?.find((a) => a.id === recordActivity.actionId);

    form.setFieldValue(['recordActivities', index, 'actionTitle'], newAction?.title);
    form.setFieldValue(['recordActivities', index, 'actionDescription'], newAction?.description);
    form.setFieldValue(['recordActivities', index, 'actionAdvice'], newAction?.advice);
  };

  const handleOnFinish = (values: any) => {
    void onFinish({
      ...values,
      recordActivities: values.recordActivities.map((recordActivity: any) => ({
        ...recordActivity,
        images: recordActivity.images['fileList']
          ? recordActivity.images?.fileList?.map((file: any) => file.response.id) || []
          : recordActivity.images?.map((image: { id: string }) => image.id) || [],
      })),
    });
  };

  return (
    <Form {...formProps} form={form} onFinish={handleOnFinish} layout="vertical" title="test">
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card>
            <Flex gap={16} wrap={'wrap'}>
              <Form.Item
                name="startTime"
                label="実際の開始時間"
                getValueFromEvent={(value) => value && format(value, 'HH:mm')}
                getValueProps={(value) => ({ value: value && parse(value, 'HH:mm', new Date()) })}
                rules={[{ required: true }]}
              >
                <DatePicker.TimePicker format="HH:mm" />
              </Form.Item>
              <Form.Item name="durationHours" label="実際のシッティング時間">
                <InputNumber step={0.5} />
              </Form.Item>
              <Form.Item name="childIds" label="子供">
                <Checkbox.Group
                  options={childList.map((child) => ({ label: child.name, value: child.id }))}
                />
              </Form.Item>
              <Form.Item
                name="educationalMaterial"
                label="使用した教材"
                style={{ minWidth: 260, width: '100%' }}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="memo" label="備考" style={{ minWidth: 260, width: '100%' }}>
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item
                name="transportationExpensePrice"
                label="交通費"
                style={{ minWidth: 260, width: '100%' }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="transportationExpenseMemo"
                label="交通費備考"
                style={{ minWidth: 260, width: '100%' }}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item
                name="transportationExpenseImages"
                style={{ minWidth: 260, width: '100%' }}
                label="領収書等"
                getValueProps={(value: any) => {
                  if (!value) {
                    return value;
                  }
                  if (value['fileList']) {
                    return value;
                  }
                  const newValue = {
                    fileList: value.map((image: { id: string; url: string }) => ({
                      uid: image.id,
                      name: image.url,
                      status: 'done',
                      url: image.url,
                      thumbUrl: image.url,
                      response: {
                        id: image.id,
                        url: image.url,
                      },
                    })),
                  };
                  return newValue;
                }}
              >
                <Upload
                  name="files"
                  action={`${import.meta.env.VITE_API_URL}/blobs`}
                  listType="picture"
                  multiple
                >
                  <Button icon={<UploadOutlined />}>アップロード</Button>
                </Upload>
              </Form.Item>
            </Flex>
          </Card>
        </Col>
        <Form.List name="recordActivities">
          {(fields, { add, move, remove }) => (
            <>
              {fields.map(({ key, name }, i) => (
                <Col key={key} xl={8} lg={12} xs={24}>
                  <Card
                    title={`活動 ${i + 1}`}
                    extra={
                      <Space>
                        <Button disabled={i === 0} onClick={() => move(i, i - 1)} block>
                          ↑
                        </Button>
                        <Button
                          disabled={i === fields.length - 1}
                          onClick={() => move(i, i + 1)}
                          block
                        >
                          ↓
                        </Button>
                        <CloseOutlined onClick={() => remove(name)} />
                      </Space>
                    }
                  >
                    <Form.Item name={[name, 'title']} label="活動タイトル">
                      <Input />
                    </Form.Item>
                    <Form.Item name={[name, 'description']} label="説明">
                      <Input />
                    </Form.Item>
                    <Form.Item name={[name, 'actionEpisode']} label="特徴的な行動のエピソード">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Flex align="end" gap={8}>
                      <Form.Item
                        name={[name, 'actionId']}
                        label="行動"
                        style={{ flex: 1, maxWidth: 'calc(100% - 80px)' }}
                      >
                        <ExperienceActionSelect allowClear />
                      </Form.Item>
                      <Button style={{ marginBottom: 24 }} onClick={() => resetRecordActivity(i)}>
                        反映
                      </Button>
                    </Flex>
                    <Form.Item name={[name, 'actionTitle']} label="行動のタイトル">
                      <Input />
                    </Form.Item>
                    <Form.Item name={[name, 'actionDescription']} label="説明">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name={[name, 'actionAdvice']} label="アドバイス">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                      name={[name, 'images']}
                      label="画像"
                      getValueProps={(value: any) => {
                        if (value['fileList']) {
                          return value;
                        }
                        const newValue = {
                          fileList: value.map((image: { id: string; url: string }) => ({
                            uid: image.id,
                            name: image.url,
                            status: 'done',
                            url: image.url,
                            thumbUrl: image.url,
                            response: {
                              id: image.id,
                              url: image.url,
                            },
                          })),
                        };
                        return newValue;
                      }}
                    >
                      <Upload
                        name="files"
                        action={`${import.meta.env.VITE_API_URL}/blobs`}
                        listType="picture"
                        multiple
                      >
                        <Button icon={<UploadOutlined />}>アップロード</Button>
                      </Upload>
                    </Form.Item>
                  </Card>
                </Col>
              ))}
              <Col xl={8} lg={12} xs={24}>
                <Button
                  onClick={() =>
                    add({
                      title: '',
                      description: '',
                      actionEpisode: '',
                      actionId: null,
                      actionTitle: '',
                      actionDescription: '',
                      actionAdvice: '',
                      images: [],
                    })
                  }
                  style={{ width: '100%' }}
                >
                  追加
                </Button>
              </Col>
            </>
          )}
        </Form.List>
        <Col xl={24} lg={24} xs={24}>
          <Card>
            <Form.Item name="sitterMessage" label="担当シッターからのメッセージ">
              <Input.TextArea rows={4} />
            </Form.Item>
            <Form.Item name="operatorMessage" label="運営からのメッセージ">
              <Input.TextArea rows={4} />
            </Form.Item>
          </Card>
        </Col>
        <Col style={{ width: '100%', position: 'sticky', bottom: 10, textAlign: 'end' }}>
          <Card size="small">
            <Flex style={{ width: '100%', gap: 20 }}>
              <Button
                onClick={() => {
                  window.open(
                    `${import.meta.env.VITE_CROWN_URL}/previews/sitting/${recordId}?token=${queryResult?.data?.data.token}`,
                  );
                }}
              >
                プレビュー
              </Button>
              <div style={{ flex: 1 }} />
              <Form.Item name="status" noStyle>
                <Radio.Group>
                  <Radio.Button value="draft">下書き</Radio.Button>
                  <Radio.Button value="published">公開</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <SaveButton {...saveButtonProps} disabled={!isDirty}>
                保存
              </SaveButton>
            </Flex>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
